import { Pencil, Plus, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function parse(value: string): string[] {
  return value
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
}

/** Blue recipient pill: avatar initial + email, with an optional remove button.
 * Shared by {@link RecipientsField} and the delivery-settings band. */
export function RecipientChip({
  email,
  onRemove,
  size = "sm",
}: {
  email: string;
  onRemove?: () => void;
  size?: "sm" | "md";
}) {
  const md = size === "md";
  return (
    <Badge
      className={cn(
        "bg-accent text-accent-foreground gap-1.5 py-0 font-semibold",
        md ? "h-[30px] pr-1.5 pl-1 text-xs" : "h-[26px] pr-1 pl-1 text-[11px]",
      )}
    >
      <span
        className={cn(
          "bg-info text-info-foreground flex items-center justify-center rounded-full font-bold",
          md ? "size-[22px] text-[10px]" : "size-5 text-[9px]",
        )}
      >
        {email[0]?.toUpperCase()}
      </span>
      <span className={cn(!md && "max-w-[90px] truncate")}>{email}</span>
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${email}`}
          onClick={onRemove}
          className="text-accent-foreground hover:bg-info/15 flex size-5 items-center justify-center rounded-full"
        >
          <X className="size-2.5" />
        </button>
      )}
    </Badge>
  );
}

/**
 * Presentational chip editor over a comma-separated recipient string. Empty →
 * dashed "Default recipients" button; non-empty → removable pills plus an add
 * affordance. The backing model stays a single comma-separated string, so the
 * notifications data layer is unchanged.
 */
export function RecipientsField({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const emails = parse(value);

  function commit(next: string[]) {
    onChange(next.join(", "));
  }

  function add() {
    const email = draft.trim();
    if (!email) return;
    if (!emails.includes(email)) commit([...emails, email]);
    setDraft("");
    setOpen(false);
  }

  function remove(email: string) {
    commit(emails.filter((e) => e !== email));
  }

  const addButton = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {emails.length === 0 ? (
          <button
            type="button"
            disabled={disabled}
            aria-label={`Edit recipients: ${label}`}
            className="border-border text-muted-foreground hover:text-foreground flex h-[34px] w-full items-center justify-between rounded-md border border-dashed px-3 text-xs font-medium disabled:opacity-50"
          >
            Default recipients
            <Pencil className="size-3.5" />
          </button>
        ) : (
          <button
            type="button"
            disabled={disabled}
            aria-label={`Add recipient: ${label}`}
            className="border-border text-muted-foreground hover:text-foreground flex size-[26px] shrink-0 items-center justify-center rounded-full border border-dashed disabled:opacity-50"
          >
            <Plus className="size-3" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64">
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <Input
            type="email"
            value={draft}
            autoFocus
            placeholder="email@firm.com"
            aria-label={`Recipient email: ${label}`}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button type="submit" size="sm" className="w-full">
            Add recipient
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );

  if (emails.length === 0) {
    return <div className="w-[220px]">{addButton}</div>;
  }

  return (
    <div className="flex w-[220px] flex-wrap items-center gap-1">
      {emails.map((email) => (
        <RecipientChip
          key={email}
          email={email}
          onRemove={disabled ? undefined : () => remove(email)}
        />
      ))}
      {!disabled && addButton}
    </div>
  );
}
